import type { ContentStatus, ContentType, FieldError } from '@apuestapr/contracts';

import {
  getDashboardContentDefinition,
  type ContentCreateByType,
  type ContentItemByType,
  type ContentUpdateByType,
} from './catalog.ts';

export type ContentFormScalar = string | null;
export type ContentFormValues = Record<string, ContentFormScalar> & {
  status: ContentStatus;
  publishAt: string | null;
  unpublishAt: string | null;
};

export const contentFieldId = (path: string): string =>
  `content-field-${path.replaceAll('.', '-')}`;

export const valuesForContent = <Type extends ContentType>(
  type: Type,
  item: ContentItemByType[Type] | null,
): ContentFormValues => {
  const definition = getDashboardContentDefinition(type);
  const values: Record<string, ContentFormScalar> = {};
  for (const field of definition.fields) {
    const existing = item?.[field.key as keyof ContentItemByType[Type]];
    if (field.control === 'keywords') {
      values[field.key] = Array.isArray(existing) ? existing.join('\n') : '';
    } else if (typeof existing === 'number') {
      values[field.key] = String(existing);
    } else if (typeof existing === 'string' || existing === null) {
      values[field.key] = existing as string | null;
    } else if (field.control === 'select') {
      values[field.key] = field.options[0]?.value ?? '';
    } else if (field.control === 'number') {
      values[field.key] = '0';
    } else {
      values[field.key] = '';
    }
  }
  return {
    ...values,
    status: item?.status ?? 'draft',
    publishAt: item?.publishAt ?? null,
    unpublishAt: item?.unpublishAt ?? null,
  } as ContentFormValues;
};

const valueForField = (
  value: ContentFormScalar,
  field: ReturnType<typeof getDashboardContentDefinition>['fields'][number],
  create: boolean,
): unknown => {
  const text = value ?? '';
  if (field.control === 'number') return Number(text);
  if (field.control === 'keywords')
    return text === '' ? [] : text.split(/\r?\n/).filter((keyword) => keyword !== '');
  if (field.nullable) return text === '' ? null : text;
  if (create && field.optionalOnCreate && text === '') return undefined;
  return text;
};

/** Converts UI-only strings into the exact write contract without sanitizing authored text. */
export const payloadForContent = <Type extends ContentType>(
  type: Type,
  values: ContentFormValues,
  create: boolean,
): ContentCreateByType[Type] | ContentUpdateByType[Type] => {
  const definition = getDashboardContentDefinition(type);
  const payload: Record<string, unknown> = {
    status: values.status,
    publishAt: values.publishAt,
    unpublishAt: values.unpublishAt,
  };
  for (const field of definition.fields) {
    const value = valueForField(values[field.key] ?? '', field, create);
    if (value !== undefined) payload[field.key] = value;
  }
  return payload as ContentCreateByType[Type] | ContentUpdateByType[Type];
};

export const normalizedContentValues = (type: ContentType, values: ContentFormValues): string =>
  JSON.stringify(payloadForContent(type, values, false));

export const errorsByField = (
  errors: readonly FieldError[],
): Readonly<Record<string, string | undefined>> => {
  const mapped: Record<string, string> = {};
  for (const error of errors) mapped[error.path] ??= error.message;
  return mapped;
};

export const errorForField = (
  errors: Readonly<Record<string, string | undefined>>,
  field: string,
): string | undefined => {
  const exact = errors[field];
  if (exact !== undefined) return exact;
  return Object.entries(errors).find(([path]) => path.startsWith(`${field}.`))?.[1];
};
